import React, { Suspense, lazy, useCallback, useState, memo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { LanguageProvider } from './context/LanguageContext';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import LanguageSwitcher from './components/LanguageSwitcher';
import './styles/App.css';

// Lazy load sections for better performance
const About = lazy(() => import(/* webpackChunkName: "about" */ './components/About'));
const Experience = lazy(() => import(/* webpackChunkName: "experience" */ './components/Experience'));
const Projects = lazy(() => import(/* webpackChunkName: "projects" */ './components/Projects'));
const Contact = lazy(() => import(/* webpackChunkName: "contact" */ './components/Contact'));
const Footer = lazy(() => import(/* webpackChunkName: "footer" */ './components/Footer'));

// Page transition variants
const pageVariants = {
  initial: {
    opacity: 0,
    y: 16,
  },
  enter: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.35,
      ease: [0.22, 1, 0.36, 1],
    },
  },
  exit: {
    opacity: 0,
    y: -8,
    transition: {
      duration: 0.2,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

// Memoized section loader
const SectionLoader = memo(() => (
  <div className="section-loader">
    <div className="loader-spinner" />
  </div>
));

SectionLoader.displayName = 'SectionLoader';

// Memoized sections to prevent unnecessary re-renders
const MemoizedHero = memo(Hero);
const MemoizedAbout = memo(About);
const MemoizedExperience = memo(Experience);
const MemoizedProjects = memo(Projects);
const MemoizedContact = memo(Contact);
const MemoizedFooter = memo(Footer);

function App() {
  const [activeSection, setActiveSection] = useState('hero');
  const showFooter = activeSection === 'contact';

  const handleNavigate = useCallback((sectionId) => {
    if (!sectionId || sectionId === activeSection) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setActiveSection(sectionId);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeSection]);

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'hero':
        return <MemoizedHero onNavigate={handleNavigate} />;
      case 'about':
        return <MemoizedAbout />;
      case 'experience':
        return <MemoizedExperience />;
      case 'projects':
        return <MemoizedProjects />;
      case 'contact':
        return <MemoizedContact />;
      default:
        return <MemoizedHero onNavigate={handleNavigate} />;
    }
  };

  return (
    <LanguageProvider>
      <div className={`App ${showFooter ? 'has-footer' : ''}`}>
        <a href="#main-content" className="skip-link">
          Skip to main content
        </a>
        <LanguageSwitcher />
        <Navbar activeSection={activeSection} onNavigate={handleNavigate} />
        <main id="main-content">
          <Suspense fallback={<SectionLoader />}>
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={activeSection}
                initial="initial"
                animate="enter"
                exit="exit"
                variants={pageVariants}
              >
                {renderActiveSection()}
              </motion.div>
            </AnimatePresence>
          </Suspense>
        </main>
        {showFooter && (
          <Suspense fallback={null}>
            <MemoizedFooter showSocial />
          </Suspense>
        )}
        <Analytics />
        <SpeedInsights />
      </div>
    </LanguageProvider>
  );
}

export default App;
